import React, { useState, useMemo, useCallback } from "react"
import { Trash2 } from "lucide-react"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"
import Pagination from "~/library/Pagination"
import NavigationLink from "~/library/NavigationLink"
import ConfirmationModal from "~/library/ConfirmationModal"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { capitalize } from "~/utils/helpers"
import { useToast } from "~/utils/Toast"
import { apiWorkspaceAccounts } from "~/routes/"
import type Account from "~/types/Account"

const ACCOUNT_DELETE_WARNING =
  "This action cannot be undone. Deleting this account will also delete its deals, associated payments, and notes. Contacts and tasks attached to this account or its deals will not be deleted, but they will be unassigned."

const paramifyQ = (q?: Record<string, string | number | null | undefined>) =>
  Object.fromEntries(
    Object.entries(q || {})
      .filter(([, v]) => v != null && v !== "")
      .map(([k, v]) => [`q[${k}]`, String(v)])
  )

const buildRansackQuery = (filters: { search: string; status: string }) => {
  const q: Record<string, string> = {}

  if (filters.search) {
    q["name_cont"] = filters.search
  }

  if (filters.status) {
    q["status_eq"] = filters.status
  }

  return q
}

const statusBadge = (status?: string) => {
  if (!status) return <span className="badge bg-secondary">Unknown</span>

  const normalized = status.toLowerCase()

  if (normalized === "active") {
    return <span className="badge bg-success">Active</span>
  }

  if (normalized === "past") {
    return <span className="badge bg-secondary">Past</span>
  }

  if (normalized === "prospect") {
    return <span className="badge bg-warning">Prospect</span>
  }

  return <span className="badge bg-light text-dark">{capitalize(status)}</span>
}

const formatMoney = (value?: number | null) => {
  if (value == null) return "—"
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const AccountList = () => {
  const { venueSlug } = getAppConfig()
  const toast = useToast()

  const [filters, setFilters] = useState({
    search: "",
    status: "",
  })
  const [accountToDelete, setAccountToDelete] = useState<Account | null>(null)

  const {
    data: accounts,
    refetch,
    metadata,
    isLoading,
  } = useFetchResource<{ accounts: Account[] }, "accounts">(
    apiWorkspaceAccounts.index.path({ venue_slug: venueSlug }),
    "accounts",
    { page: "1", ...paramifyQ(buildRansackQuery(filters)) }
  )

  const handleFilterChange = (field: string, value: string) => {
    const updated = { ...filters, [field]: value }
    setFilters(updated)
    refetch({ page: "1", ...paramifyQ(buildRansackQuery(updated)) })
  }

  const handlePageChange = (page: string) => {
    refetch({ page, ...paramifyQ(buildRansackQuery(filters)) })
  }

  const handleDeleteAccount = useCallback(
    async (account: Account) => {
      try {
        const url = apiWorkspaceAccounts.destroy.path({
          venue_slug: venueSlug,
          id: account.prefixId,
        })

        const response = await fetch(url, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        })

        if (response.ok) {
          toast("Account deleted successfully", "success")
          setAccountToDelete(null)
          refetch({
            page: metadata?.page?.toString() || "1",
            ...paramifyQ(buildRansackQuery(filters)),
          })
        } else {
          toast("Error deleting account", "danger")
        }
      } catch (error) {
        console.error("Error:", error)
        toast("Error deleting account", "danger")
      }
    },
    [filters, metadata?.page, refetch, toast, venueSlug]
  )

  const confirmDeleteAccount = useCallback(() => {
    if (!accountToDelete) return

    handleDeleteAccount(accountToDelete)
  }, [accountToDelete, handleDeleteAccount])

  const columns = useMemo(
    () => [
      {
        key: "name",
        header: "Account Name",
        formatter: (name: string, row: Account) => (
          <NavigationLink type="accounts" to={`/accounts/${row.prefixId}`}>
            {name}
          </NavigationLink>
        ),
      },
      {
        key: "accountType",
        header: "Type",
        formatter: (_: unknown, row: Account) => row.accountType?.name || "—",
      },
      {
        key: "status",
        header: "Status",
        formatter: (value: string) => statusBadge(value),
      },
      {
        key: "industry",
        header: "Industry",
        formatter: (value: string) => value || "—",
      },
      {
        key: "historicalSpend",
        header: "Historical Spend",
        formatter: (value: number) => formatMoney(value),
      },
      {
        key: "lifetimeValue",
        header: "Lifetime Value",
        formatter: (value: number) => formatMoney(value),
      },
      {
        key: "id",
        header: "Actions",
        formatter: (_: string, row: Account) => (
          <button
            type="button"
            className="btn btn-sm btn-link text-danger p-1"
            onClick={() => setAccountToDelete(row)}
            title="Delete Account"
            aria-label={`Delete ${row.name}`}
          >
            <Trash2 size={16} />
          </button>
        ),
      },
    ],
    []
  )

  return (
    <LoadingWrapper isLoading={isLoading}>
      <PageTitle title="Accounts" newItemPath="/accounts/new" />

      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center mb-3 gap-3 flex-wrap">
            <div className="ms-auto d-flex gap-2 flex-wrap align-items-center">
              <select
                className="form-select w-auto"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="prospect">Prospect</option>
                <option value="active">Active</option>
                <option value="past">Past</option>
              </select>

              <input
                type="text"
                className="form-control w-auto"
                placeholder="Search accounts"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>
          </div>

          <CatchCard className="card-dark-theme">
            <CatchTable
              key={`${filters.search}-${filters.status}`}
              data={accounts}
              emptyMessage="No Accounts"
              columns={columns}
            />
            {metadata && (
              <Pagination
                metadata={metadata}
                onPageChange={(page: number) => handlePageChange(page.toString())}
              />
            )}
          </CatchCard>
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!accountToDelete}
        onClose={() => setAccountToDelete(null)}
        onConfirm={confirmDeleteAccount}
        title={`Delete Account "${accountToDelete?.name}"?`}
        description={ACCOUNT_DELETE_WARNING}
        confirmButtonVariant="danger"
      />
    </LoadingWrapper>
  )
}

export default AccountList
